import * as wjcCore from '@mescius/wijmo';
import {Component, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ToasterService} from 'angular2-toaster';
import {DataService} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {TranslocoService} from '@jsverse/transloco';

interface SearchRow {
    customerId: any;
    customerName: string;
    regNo: string;        // 顧客.登録番号
    uuid: string;
    auctionDate: string;  // yyyy-MM-dd
    total: number | null; // AC_Invoice.total (請求が無ければ null)
    status: string;       // AC_Invoice.status
    fileName: string;
    size: number;
    sizeText: string;
    createdText: string;
    checked: boolean;
}

/**
 * kobe-ac 帳票検索 (旧「ファイル管理」を置き換え)。管理者向け。
 * 顧客フォルダの売買台帳PDF (/rest/kobeac/ledger/list) を全オークション横断で読み、
 * AC_Invoice (合計・請求区分) と顧客の登録番号を突合して、キーワード・顧客・期間・
 * 請求区分で絞り込む。単体/複数(ZIP)ダウンロードは売買台帳一覧と同じ API。
 */
@Component({
    standalone: false,
    selector: 'kobeac-ledger-search',
    templateUrl: 'ledger-search.component.html',
})
export class KobeacLedgerSearchComponent implements OnInit {
    private base = ActivePromotionConfig.SERVER_URL + '/rest/kobeac/ledger';
    private rows: SearchRow[] = [];
    cv: wjcCore.CollectionView = new wjcCore.CollectionView([], {pageSize: 100});
    pageSize = 100;
    loading = false;

    private readonly PAGE_SIZES = [100, 300, 1000];

    keyword = '';
    customerUuid = '';
    dateFrom = '';  // yyyy-MM-dd
    dateTo = '';
    status = '';

    customers: { uuid: string, label: string }[] = [];
    statuses: string[] = [];

    constructor(private http: HttpClient, private dataService: DataService,
                private toasterService: ToasterService, private transloco: TranslocoService) {
    }

    ngOnInit(): void {
        this.load();
    }

    /** PDF 一覧と AC_Invoice・顧客情報を読み直して行を作る。 */
    load(): void {
        this.loading = true;
        this.http.get(this.base + '/list').subscribe({
            next: (res: any) => this.loadInvoices((res && res.files) ? res.files : []),
            error: () => this.loadFailed(),
        });
    }

    /** 請求は「顧客 uuid|日付」と「顧客ID|日付」の両方で引けるようにする。 */
    private loadInvoices(files: any[]): void {
        const sql = 'SELECT customer.uuid as uuid, customer.ID as customerId, customerName, '
            + 'auction.`日付` as auctionDate, total, status FROM AC_Invoice';
        this.dataService.query({sql: sql, count: sql, fetchPlan: '*:0'}, ActivePromotionConfig.SERVER_URL).subscribe({
            next: (res: any) => {
                const invoices: { [key: string]: any } = {};
                for (const v of (wjcCore.isArray(res.results) ? res.results : [])) {
                    const date = this.digits(v.auctionDate);
                    if (v.uuid != null) invoices['u' + this.ridStr(v.uuid) + '|' + date] = v;
                    if (v.customerId != null) invoices['i' + v.customerId + '|' + date] = v;
                }
                this.loadCustomers(files, invoices);
            },
            error: () => this.loadFailed(),
        });
    }

    private loadCustomers(files: any[], invoices: { [key: string]: any }): void {
        const sql = 'SELECT uuid, ID as customerId, `名称` as name, `登録番号` as regNo FROM AC_Customer';
        this.dataService.query({sql: sql, count: sql, fetchPlan: '*:0'}, ActivePromotionConfig.SERVER_URL).subscribe({
            next: (res: any) => {
                const customers: { [key: string]: any } = {};
                for (const c of (wjcCore.isArray(res.results) ? res.results : [])) {
                    if (c.uuid != null) customers['u' + this.ridStr(c.uuid)] = c;
                    if (c.customerId != null) customers['i' + c.customerId] = c;
                }
                this.buildRows(files, invoices, customers);
            },
            error: () => this.loadFailed(),
        });
    }

    /**
     * フォルダ (uuid) が顧客マスタと一致しない PDF (顧客の再登録・DB 復元など) もあるため、
     * uuid で引けなければファイル名先頭の顧客ID (<ID>_売買台帳_<yyyyMMdd>.pdf) で顧客・請求を引く。
     */
    private buildRows(files: any[], invoices: { [key: string]: any }, customerMap: { [key: string]: any }): void {
        const customers: { [uuid: string]: { uuid: string, label: string, id: number } } = {};
        const statuses: string[] = [];
        this.rows = files.map((f: any) => {
            const m = /^(\d+)_/.exec(f.fileName || '');
            const idInName = m ? m[1] : '';
            const cust = customerMap['u' + f.uuid] || (idInName ? customerMap['i' + idInName] : null);
            const date = this.digits(f.auctionDate);
            const custId = f.customerId != null ? f.customerId
                : (cust && cust.customerId != null ? cust.customerId : idInName);
            const inv = invoices['u' + f.uuid + '|' + date] || (custId !== '' ? invoices['i' + custId + '|' + date] : null);
            const name = f.customerName || (inv && inv.customerName) || (cust && cust.name) || '';
            const status = inv && inv.status != null ? String(inv.status) : '';
            if (status && statuses.indexOf(status) < 0) statuses.push(status);
            if (!customers[f.uuid]) {
                const id = parseInt(String(custId).replace(/\D/g, ''), 10);
                customers[f.uuid] = {
                    uuid: f.uuid,
                    label: ((custId !== '' ? custId + ' ' : '') + name).trim() || f.uuid,
                    id: isNaN(id) ? Number.MAX_SAFE_INTEGER : id,
                };
            }
            return {
                customerId: custId === '' ? null : custId,
                customerName: name,
                regNo: cust && cust.regNo != null ? String(cust.regNo) : '',
                uuid: f.uuid,
                auctionDate: f.auctionDate || '',
                total: inv && inv.total != null ? Number(inv.total) : null,
                status: status,
                fileName: f.fileName,
                size: f.size,
                sizeText: this.convertSize(f.size),
                createdText: this.formatDateTime(f.created),
                checked: false,
            } as SearchRow;
        });
        // /list はオークション日の新しい順・顧客名順で返る
        this.customers = Object.keys(customers).map((k) => customers[k])
            .sort((a, b) => (a.id - b.id) || a.label.localeCompare(b.label));
        this.statuses = statuses.sort();
        const cv = new wjcCore.CollectionView(this.rows, {pageSize: this.pageSize});
        cv.filter = (item: SearchRow) => this.matches(item);
        this.cv = cv;
        this.loading = false;
    }

    private loadFailed(): void {
        this.loading = false;
        this.toasterService.pop('error', this.transloco.translate('kobeac.ledger_search'), this.transloco.translate('kobeac.list_load_failed'));
    }

    /** キーワードは空白区切りの AND。各語が顧客ID・顧客名・登録番号・ファイル名・請求区分のどれかに含まれれば一致。 */
    private matches(item: SearchRow): boolean {
        if (this.customerUuid && item.uuid !== this.customerUuid) return false;
        if (this.dateFrom && (!item.auctionDate || item.auctionDate < this.dateFrom)) return false;
        if (this.dateTo && (!item.auctionDate || item.auctionDate > this.dateTo)) return false;
        if (this.status && item.status !== this.status) return false;
        const words = this.keyword.trim().toLowerCase().split(/[\s　]+/).filter((w) => w);
        if (words.length) {
            const text = [item.customerId, item.customerName, item.regNo, item.fileName, item.status]
                .map((v) => (v == null ? '' : String(v))).join('\n').toLowerCase();
            for (const w of words) if (text.indexOf(w) < 0) return false;
        }
        return true;
    }

    applyFilter(): void {
        if (this.cv) this.cv.refresh();
    }

    clearFilter(): void {
        this.keyword = '';
        this.customerUuid = '';
        this.dateFrom = '';
        this.dateTo = '';
        this.status = '';
        this.applyFilter();
    }

    /** 表示件数 (ledger-list と同じく menu.selectedIndex で解決)。 */
    onPageSizeChanged(menu?: any): void {
        const idx = menu != null && wjcCore.isInt(menu.selectedIndex) ? menu.selectedIndex : -1;
        if (idx >= 0 && idx < this.PAGE_SIZES.length) {
            this.pageSize = this.PAGE_SIZES[idx];
        }
        if (this.cv) {
            this.cv.pageSize = this.pageSize;
        }
    }

    get selectedCount(): number {
        return this.rows.filter((r) => r.checked).length;
    }

    /** 現在の絞り込み結果 (全ページ) を全選択/解除 */
    setAllChecked(checked: boolean): void {
        for (const r of this.cv.items as SearchRow[]) r.checked = checked;
    }

    download(row: SearchRow): void {
        this.http.get(this.base + '/download', {params: {uuid: row.uuid, file: row.fileName}, responseType: 'blob'}).subscribe({
            next: (blob: Blob) => this.saveBlob(blob, row.fileName),
            error: () => this.toasterService.pop('error', this.transloco.translate('kobeac.ledger_search'), this.transloco.translate('kobeac.download_failed')),
        });
    }

    downloadSelected(): void {
        const items = this.rows.filter((r) => r.checked).map((r) => ({uuid: r.uuid, file: r.fileName}));
        if (items.length === 0) {
            this.toasterService.pop('warning', this.transloco.translate('kobeac.ledger_search'), this.transloco.translate('kobeac.select_file'));
            return;
        }
        this.http.post(this.base + '/download-zip', {items: items}, {responseType: 'blob'}).subscribe({
            next: (blob: Blob) => this.saveBlob(blob, 'baibaidaicho.zip'),
            error: () => this.toasterService.pop('error', this.transloco.translate('kobeac.ledger_search'), this.transloco.translate('kobeac.zip_download_failed')),
        });
    }

    private saveBlob(blob: Blob, name: string): void {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }

    private digits(v: any): string {
        return String(v == null ? '' : v).replace(/\D/g, '').slice(0, 8);
    }

    private ridStr(v: any): string {
        if (v == null) return '';
        if (wjcCore.isObject(v) && v['@rid']) return v['@rid'];
        return String(v);
    }

    private formatDateTime(millis: number): string {
        if (!millis || millis <= 0) {
            return '';
        }
        const d = new Date(millis);
        const p = (n: number) => (n < 10 ? '0' + n : '' + n);
        return d.getFullYear() + '/' + p(d.getMonth() + 1) + '/' + p(d.getDate())
            + ' ' + p(d.getHours()) + ':' + p(d.getMinutes());
    }

    convertSize(size: number): string {
        return size < 1024000 ? (size / 1024).toFixed(1) + ' KB' : (size / 1024000).toFixed(2) + ' MB';
    }
}
